import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NAV } from '@/constants/testIds';
import { AAWTA } from '@/lib/aawta';

const links = [
  { id: 'home', label: 'Home', href: '#home', testId: NAV.home },
  { id: 'summit', label: 'Summit Overview', href: '#summit', testId: NAV.summitOverview },
  { id: 'agenda', label: 'Agenda', href: '#agenda', testId: NAV.agenda },
  { id: 'speakers', label: 'Speakers', href: '#speakers', testId: NAV.speakers },
  { id: 'sponsors', label: 'Sponsor Showcase', href: '#sponsor-showcase', testId: NAV.sponsorShowcase },
  { id: 'exhibitors', label: 'Exhibitors', href: '#exhibitors', testId: NAV.exhibitorDirectory },
  { id: 'gallery', label: 'Gallery', href: '#gallery', testId: NAV.gallery },
  { id: 'faq', label: 'FAQ', href: '#faq', testId: NAV.faq },
  { id: 'venue', label: 'Venue & Hotels', href: '#venue', testId: NAV.venueHotels },
  { id: 'downloads', label: 'Downloads', href: '#downloads', testId: NAV.downloads },
  { id: 'participate', label: 'Partner Participation Form', to: '/participate', testId: 'nav-link-participate' },
  { id: 'volunteer', label: 'Volunteer', to: '/volunteer', testId: 'nav-link-volunteer' },
  { id: 'media', label: 'Media', href: '#media', testId: 'nav-link-media' },
  { id: 'contact', label: 'Contact', href: '#contact', testId: NAV.contact },
];

const openExternal = (url) => window.open(url, '_blank', 'noopener,noreferrer');

export const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onLanding = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const closeMobile = () => setOpen(false);

  return (
    <header
      data-testid={NAV.root}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'backdrop-blur-xl bg-white/92 border-b border-slate-200 shadow-[0_2px_20px_rgba(15,30,86,0.05)]'
          : 'backdrop-blur-md bg-white/75 border-b border-white/40'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        <div className="h-20 flex items-center justify-between gap-4">
          <a href="#home" className="flex items-center gap-3 shrink-0" data-testid="nav-logo-link">
            <img
              src={AAWTA.logoUrl}
              alt="AAWTA logo"
              className="h-11 w-11 rounded-full object-cover ring-2 ring-white shadow-aawta-soft"
            />
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-display font-bold text-[15px] text-aawta-navy">AAWTA</span>
              <span className="text-[10px] tracking-[0.18em] uppercase text-aawta-muted font-medium">
                Workforce Collaborative
              </span>
            </div>
          </a>

          <nav className="hidden 2xl:flex items-center gap-0.5">
            {links.map((l) => {
              const cls = "px-2.5 py-2 text-[13px] font-medium text-aawta-navy/80 hover:text-aawta-magenta transition-colors duration-200 relative group whitespace-nowrap";
              if (l.to) {
                return (
                  <Link key={l.id} to={l.to} data-testid={l.testId} className={cls}>
                    {l.label}
                    <span className="absolute left-2.5 right-2.5 -bottom-0.5 h-[2px] bg-aawta-magenta scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
                  </Link>
                );
              }
              const href = onLanding ? l.href : `/${l.href}`;
              return (
                <a key={l.id} href={href} data-testid={l.testId} className={cls}>
                  {l.label}
                  {l.badge && (
                    <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-aawta-magenta/10 text-aawta-magenta">
                      {l.badge}
                    </span>
                  )}
                  <span className="absolute left-2.5 right-2.5 -bottom-0.5 h-[2px] bg-aawta-magenta scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
                </a>
              );
            })}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => openExternal(AAWTA.eventbriteUrl)}
              data-testid={NAV.registerNow}
              className="rounded-full text-aawta-navy hover:bg-aawta-navy/5 hover:text-aawta-navy font-medium"
            >
              Register Now
              <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
            </Button>
            <Button
              onClick={() => openExternal(AAWTA.zeffySponsorUrl)}
              data-testid={NAV.becomeSponsor}
              className="rounded-full bg-aawta-magenta hover:bg-aawta-magenta-hover text-white font-medium px-5 shadow-aawta-magenta hover:-translate-y-0.5 transition-all duration-200"
            >
              Become a Sponsor
              <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
            </Button>
          </div>

          <button
            className="2xl:hidden inline-flex items-center justify-center p-2 rounded-full hover:bg-slate-100"
            aria-label="Toggle menu"
            data-testid={NAV.mobileToggle}
            onClick={() => setOpen(!open)}
          >
            {open ? <X className="h-6 w-6 text-aawta-navy" /> : <Menu className="h-6 w-6 text-aawta-navy" />}
          </button>
        </div>

        {open && (
          <div className="2xl:hidden pb-6 pt-2 border-t border-slate-200 max-h-[80vh] overflow-y-auto">
            <div className="flex flex-col gap-1">
              {links.map((l) => {
                const cls = "px-3 py-3 rounded-lg text-[15px] font-medium text-aawta-navy hover:bg-slate-50 flex items-center justify-between";
                if (l.to) {
                  return (
                    <Link key={l.id} to={l.to} onClick={closeMobile} data-testid={`mobile-${l.testId}`} className={cls}>
                      <span>{l.label}</span>
                    </Link>
                  );
                }
                const href = onLanding ? l.href : `/${l.href}`;
                return (
                  <a key={l.id} href={href} onClick={closeMobile} data-testid={`mobile-${l.testId}`} className={cls}>
                    <span>{l.label}</span>
                    {l.badge && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-aawta-magenta/10 text-aawta-magenta">
                        {l.badge}
                      </span>
                    )}
                  </a>
                );
              })}
              <div className="flex flex-col gap-2 pt-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    closeMobile();
                    openExternal(AAWTA.eventbriteUrl);
                  }}
                  className="rounded-full border-aawta-navy text-aawta-navy"
                >
                  Register Now
                </Button>
                <Button
                  onClick={() => {
                    closeMobile();
                    openExternal(AAWTA.zeffySponsorUrl);
                  }}
                  className="rounded-full bg-aawta-magenta hover:bg-aawta-magenta-hover text-white"
                >
                  Become a Sponsor
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navigation;
